import {
  PlatformInfoBannerVariant,
  PlatformInfoBannerSettingsResponseDto,
} from "@/lib/api/interfaces/platform-settings.interface";
import {
  usePlatformSettingsInfoBannerQuery,
  usePlatformSettingsUpdateInfoBanner,
} from "@/lib/api/requests/platform-settings.requests";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils/cn";
import { PageHeader, PageLayout, PageTitle } from "@/util-components/page-layout";
import { GlobalInfoBanner } from "@/util-components/root/global-info-banner";
import { Button } from "@/ui-components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Switch } from "@/ui-components/ui/switch";
import { Textarea } from "@/ui-components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { BellRing, Loader2, ShieldAlert } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const infoBannerFormSchema = z
  .object({
    enabled: z.boolean(),
    variant: z.nativeEnum(PlatformInfoBannerVariant),
    title: z.string().max(120, "Bitte maximal 120 Zeichen verwenden."),
    content: z.string().max(2000, "Bitte maximal 2000 Zeichen verwenden."),
  })
  .superRefine((value, ctx) => {
    if (!value.enabled) {
      return;
    }

    if (!value.title.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Bitte einen Titel für das aktive Banner angeben.",
        path: ["title"],
      });
    }

    if (!value.content.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Bitte einen Detailtext für das aktive Banner angeben.",
        path: ["content"],
      });
    }
  });

type InfoBannerFormValues = z.infer<typeof infoBannerFormSchema>;

const defaultValues: InfoBannerFormValues = {
  enabled: false,
  variant: PlatformInfoBannerVariant.INFO,
  title: "",
  content: "",
};

function mapSettingsToFormValues(
  settings?: PlatformInfoBannerSettingsResponseDto,
): InfoBannerFormValues {
  if (!settings) {
    return defaultValues;
  }

  return {
    enabled: settings.enabled,
    variant: settings.variant,
    title: settings.title ?? "",
    content: settings.content ?? "",
  };
}

const Admin = () => {
  const { data, isLoading } = usePlatformSettingsInfoBannerQuery();
  const updateInfoBanner = usePlatformSettingsUpdateInfoBanner();

  const form = useForm<InfoBannerFormValues>({
    resolver: zodResolver(infoBannerFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (data) {
      form.reset(mapSettingsToFormValues(data));
    }
  }, [data, form]);

  const previewValues = form.watch();

  const onSubmit = async (values: InfoBannerFormValues) => {
    try {
      await updateInfoBanner.mutateAsync({
        enabled: values.enabled,
        variant: values.variant,
        title: values.title.trim(),
        content: values.content.trim(),
      });
      toast.success("Infobanner gespeichert");
    } catch (error) {
      toast.error("Infobanner konnte nicht gespeichert werden", { error });
    }
  };

  return (
    <PageLayout basePath="/admin">
      <PageHeader>
        <PageTitle
          title="Admin"
          icon={ShieldAlert}
          iconColor="text-red-600"
          iconBg="bg-red-50"
        />
      </PageHeader>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <Card className="overflow-hidden border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100 bg-white">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-100 bg-amber-50">
                <BellRing className="h-6 w-6 text-amber-600" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-xl text-gray-950">Globales Infobanner</CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  Dieses Banner wird allen internen und Customer-Usern im Frontend angezeigt, sobald es aktiviert ist.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="enabled"
                      render={({ field }) => (
                        <FormItem className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                              <FormLabel isOptional={false}>Banner aktivieren</FormLabel>
                              <FormDescription>
                                Aktiviert den Banner global im gesamten eingeloggten Frontend.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="variant"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel isOptional={false}>Status / Farbe</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                                <SelectValue placeholder="Status wählen" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={PlatformInfoBannerVariant.ERROR}>Fehler / Rot</SelectItem>
                              <SelectItem value={PlatformInfoBannerVariant.INFO}>Info / Gelb</SelectItem>
                              <SelectItem value={PlatformInfoBannerVariant.SUCCESS}>Success / Grün</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Die Farbe bestimmt die globale Wirkung des Banners im Frontend.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel isOptional={false}>Titel</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Zum Beispiel: Geplante Wartung heute Abend"
                              className="h-11 rounded-xl border-gray-200"
                            />
                          </FormControl>
                          <FormDescription>
                            Der Titel wird direkt im schlanken Banner-Streifen angezeigt.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="content"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel isOptional={false}>Detailtext</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              rows={7}
                              placeholder="Dieser Text erscheint, wenn ein User das Banner öffnet."
                              className="min-h-[180px] rounded-2xl border-gray-200 bg-white"
                            />
                          </FormControl>
                          <FormDescription>
                            Der Detailtext wird nach Klick auf das Banner in einer kompakten Detailansicht geöffnet.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                        Live-Vorschau
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        So erscheint der Banner oberhalb des Grundlayouts.
                      </p>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                      {previewValues.title.trim() && previewValues.content.trim() ? (
                        <GlobalInfoBanner
                          banner={{
                            enabled: previewValues.enabled,
                            variant: previewValues.variant,
                            title: previewValues.title.trim(),
                            content: previewValues.content.trim(),
                            updatedAt: data?.updatedAt ?? null,
                          }}
                          dismissible={false}
                          previewLabel={previewValues.enabled ? "Aktiv" : "Inaktiv"}
                        />
                      ) : (
                        <div className="p-6 text-sm text-gray-500">
                          Titel und Detailtext ergänzen, um die Vorschau zu sehen.
                        </div>
                      )}

                      <div className="border-t border-gray-100 px-5 py-4">
                        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                          <p className="text-sm font-medium text-gray-900">Hinweis zum Dismiss</p>
                          <p className="mt-1 text-sm text-gray-500">
                            User können den Banner mit dem <span className="font-medium text-gray-700">X</span> lokal schließen. Nach jeder Änderung erscheint er wieder.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => form.reset(mapSettingsToFormValues(data))}
                    disabled={isLoading || updateInfoBanner.isPending || !form.formState.isDirty}
                  >
                    Zurücksetzen
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || updateInfoBanner.isPending || !form.formState.isDirty}
                    className={cn(
                      "min-w-[160px] rounded-xl text-white shadow-sm",
                      previewValues.variant === PlatformInfoBannerVariant.ERROR && "bg-red-600 hover:bg-red-700",
                      previewValues.variant === PlatformInfoBannerVariant.INFO && "bg-amber-500 hover:bg-amber-600",
                      previewValues.variant === PlatformInfoBannerVariant.SUCCESS && "bg-emerald-600 hover:bg-emerald-700",
                    )}
                  >
                    {updateInfoBanner.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Wird gespeichert...
                      </>
                    ) : (
                      "Infobanner speichern"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Admin;
